import { useLocation } from "react-router-dom"

import type { RouteOverrides } from "@/api/models"

import { useRoutes } from "@/contexts/use-routes"
import { useIsPlanFeatureLocked } from "@/features/fair-use/hooks/use-plan-entitlements"
import { SettingsService } from "@/services/settings-service"

import { useFeatureEnabled } from "./use-feature-enabled"

const EMPTY_ROUTE_OVERRIDES: RouteOverrides = {}

export { usePrismMemory } from "./use-prism-memory"

/**
 * Tri-state availability for a feature that a fair-use PLAN can lock
 * (round 2, #3087).
 *
 * The distinction the old boolean could not make:
 * - `"hidden"` — the tenant does not have this at all (admin feature off, or
 *   the route overrides it away). Nothing to offer, so nothing is rendered.
 * - `"locked"` — the tenant HAS it, this user's plan does not. The control
 *   stays visible, disabled, with an upgrade hint: hiding it would tell the
 *   user the product lacks a feature their colleague is using.
 * - `"available"` — usable.
 */
export type FeatureAvailability = "available" | "locked" | "hidden"

/**
 * Hook to get route-specific overrides for the current route.
 * Returns the overrides object from the current route configuration.
 *
 * @returns RouteOverrides object with feature toggles and configuration
 */
export function useRouteOverrides(): RouteOverrides {
  const { routes } = useRoutes()
  const location = useLocation()

  // Find the route with the longest matching path to handle nested routes correctly
  // e.g., prefer /custom-variant over / when on /custom-variant
  const currentRoute = routes
    .filter((route) => location.pathname.startsWith(route.path))
    .sort((a, b) => b.path.length - a.path.length)[0]

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return currentRoute?.overrides ?? EMPTY_ROUTE_OVERRIDES
}

/**
 * Check if web search is enabled for the current route.
 * Respects route-level overrides, global settings, and admin tool toggles.
 *
 * @returns true if web search should be available
 */
export function useWebSearchEnabled(): boolean {
  const overrides = useRouteOverrides()
  const settingsService = SettingsService.getInstance()

  // If explicitly disabled in route, return false
  if (overrides.disableWebSearch === true) {
    return false
  }

  // Check if web_search tool is enabled by admin
  if (!settingsService.settings.enabled_tools?.includes("web_search")) {
    return false
  }

  // Otherwise check global setting
  return settingsService.settings.showWebSearchOption
}

/**
 * Check if workflows are enabled for the current route.
 * Respects both route-level overrides and global feature flags.
 *
 * @returns true if workflows should be available
 */
export function useWorkflowsEnabled(): boolean {
  const overrides = useRouteOverrides()
  const globalEnabled = useFeatureEnabled("explorer_flows")

  // If explicitly disabled in route, return false
  if (overrides.disableWorkflows === true) {
    return false
  }

  return globalEnabled
}

/**
 * Check if prompt library is enabled for the current route.
 * Respects both route-level overrides and global feature flags.
 *
 * @returns true if prompts should be available
 */
export function usePromptsEnabled(): boolean {
  const overrides = useRouteOverrides()
  const globalEnabled = useFeatureEnabled("explorer_prompts")

  // If explicitly disabled in route, return false
  if (overrides.disablePrompts === true) {
    return false
  }

  return globalEnabled
}

/**
 * Check if agent mode is enabled for the current route.
 * Respects both route-level overrides and global feature flags.
 *
 * @returns true if agent mode should be available
 */
export function useAgentModeEnabled(): boolean {
  const overrides = useRouteOverrides()
  const globalEnabled = useFeatureEnabled("scholar")

  // If explicitly disabled in route, return false
  if (overrides.disableAgent === true) {
    return false
  }

  return globalEnabled
}

/**
 * Deep Explorer availability for the current route, as a tri-state.
 *
 * Respects route-level overrides (`disableDeep`), the `explorer_deep` admin
 * feature, and — new in fair use round 2 — the user's plan entitlement.
 *
 * @returns "hidden" (route/admin), "locked" (plan), or "available"
 */
export function useDeepExplorerAvailability(): FeatureAvailability {
  const overrides = useRouteOverrides()
  const globalEnabled = useFeatureEnabled("explorer_deep")
  const planLocked = useIsPlanFeatureLocked("deep")

  // A route override and the admin feature flag both mean "this tenant/route
  // has no Deep at all" — hidden, not locked. Only the plan produces a lock,
  // and it is evaluated LAST so a locked-down route never advertises an
  // upgrade for something the route forbids anyway.
  if (overrides.disableDeep === true) {
    return "hidden"
  }
  if (!globalEnabled) {
    return "hidden"
  }
  return planLocked ? "locked" : "available"
}

/**
 * Whether a Deep Explorer turn may actually be STARTED.
 *
 * Deliberately false for a plan-locked user as well as a hidden one: every
 * caller of this hook (the `enableDeep` kill-switch effect in
 * `use-ai-explorer-chat`, the auto-enable effect on the Explorer page) is
 * deciding whether to route a send to `/ws/explorer-deep`, which would come
 * back as a `plan_upgrade_required` refusal. The toolbar asks
 * {@link useDeepExplorerAvailability} instead, because it is the one surface
 * that must tell "hidden" from "locked".
 */
export function useDeepExplorerEnabled(): boolean {
  return useDeepExplorerAvailability() === "available"
}

/**
 * Check if high reasoning is enabled for the current route.
 * Respects route-level overrides.
 *
 * @returns true if high reasoning should be available
 */
export function useHighReasoningEnabled(): boolean {
  const overrides = useRouteOverrides()

  // If explicitly disabled in route, return false
  if (overrides.disableHighReasoning === true) {
    return false
  }

  return true
}

/**
 * Model and effort the current route preselects (`defaultModelId` /
 * `defaultReasoningEffort`, #3327). Each is null when the route sets none.
 */
export function useRouteModelDefaults(): { modelId: string | null; effort: string | null } {
  const overrides = useRouteOverrides()
  const text = (value: unknown) => (typeof value === "string" && value.trim() ? value.trim() : null)
  return {
    modelId: text(overrides.defaultModelId),
    effort: text(overrides.defaultReasoningEffort)?.toLowerCase() ?? null,
  }
}

/**
 * Check if the personalized chat home is enabled for the current route.
 * Respects route-level overrides. Locked-down routes can force the plain
 * home (logo + greeting + example questions) via `disablePersonalizedHome`.
 *
 * @returns true if the personalized home may be shown
 */
export function usePersonalizedHomeEnabled(): boolean {
  const overrides = useRouteOverrides()

  if (overrides.disablePersonalizedHome === true) {
    return false
  }

  return true
}

/**
 * Check if M365 search is enabled for the current route.
 * Respects route-level overrides, global settings, and admin tool toggles.
 *
 * @returns true if M365 search should be available
 */
export function useM365Enabled(): boolean {
  const overrides = useRouteOverrides()

  // If explicitly disabled in route, return false
  if (overrides.disableM365 === true) {
    return false
  }

  return isM365ToolEnabled()
}

/**
 * Tenant-level Microsoft 365 availability: the admin has enabled the tool and
 * the global option is on. Ignores route overrides, so surfaces that are not
 * tied to a route (the onboarding wizard's consent step) can ask whether the
 * tenant has M365 at all. Tolerates an uninitialized settings singleton.
 */
export function isM365ToolEnabled(): boolean {
  const settings = SettingsService.getInstance().settings as
    | { enabled_tools?: string[]; showM365Option?: boolean }
    | undefined
  if (!settings?.enabled_tools?.includes("microsoft_365_search")) {
    return false
  }
  return settings.showM365Option === true
}

/**
 * Check if voice mode is enabled for the current route.
 * Respects route-level overrides.
 *
 * Deliberately NOT plan-aware, unlike its Deep twin: callers pass this
 * straight to `hideVoiceMode`, and hiding the composer's microphone would
 * remove the only place a plan lock and its upgrade hint can be shown. The
 * composer locks the button itself from
 * `useIsPlanFeatureLocked("voice")` — which needs neither a Router nor a
 * QueryClient, both of which the composer renders without — and a locked user
 * who reaches the socket anyway is refused by `voice_fair_use_preflight`.
 *
 * @returns true if voice mode should be available
 */
export function useVoiceModeEnabled(): boolean {
  const overrides = useRouteOverrides()

  if (overrides.disableVoiceMode === true) {
    return false
  }

  return true
}

/**
 * Check if ServiceDesk is enabled for the current route.
 * Respects route-level overrides and admin tool toggles.
 *
 * @returns true if ServiceDesk should be available
 */
export function useServiceDeskEnabled(): boolean {
  const overrides = useRouteOverrides()
  const settingsService = SettingsService.getInstance()

  if (overrides.disableServiceDesk === true) {
    return false
  }

  return settingsService.settings.enabled_tools?.includes("service_desk") ?? false
}

/**
 * Check if Netbox is enabled for the current route.
 * Respects route-level overrides and admin tool toggles.
 *
 * @returns true if Netbox should be available
 */
export function useNetboxEnabled(): boolean {
  const overrides = useRouteOverrides()
  const settingsService = SettingsService.getInstance()

  if (overrides.disableNetbox === true) {
    return false
  }

  return settingsService.settings.enabled_tools?.includes("netbox") ?? false
}

/**
 * Check if the X-One tool is enabled for the current route.
 * Respects route-level overrides and admin tool toggles.
 *
 * @returns true if the X-One tool should be available
 */
export function useXOneEnabled(): boolean {
  const overrides = useRouteOverrides()
  const settingsService = SettingsService.getInstance()

  if (overrides.disableXOne === true) {
    return false
  }

  // Admin must have the tool enabled for the explorer agent...
  if (!settingsService.settings.enabled_tools?.includes("x_one")) {
    return false
  }

  // ...AND the backend must report X-One as configured (XONE_BASE_URL +
  // XONE_SERVICE_KEY present). x_one is discovered regardless of config, so
  // without this gate an unconfigured deployment would surface a default-on
  // toggle that sends use_x_one to a tool that can only answer "not configured."
  return settingsService.settings.showXOneOption ?? false
}

/**
 * Check if the plugin store submenu is available for the current route.
 * A route-level `disablePlugins` wins; otherwise `/api/config` reports
 * whether this stamp has the plugin store deployed. Tenant enablement and
 * the user's own installations are read from the catalog itself, so an
 * empty or disabled store still renders an explanatory submenu.
 *
 * @returns true if the Plugins submenu should be offered
 */
export function usePluginsEnabled(): boolean {
  const overrides = useRouteOverrides()
  const settingsService = SettingsService.getInstance()

  if (overrides.disablePlugins === true) {
    return false
  }

  return settingsService.settings.showPluginsOption ?? false
}
